import { c as _c } from "react/compiler-runtime";
// The pages as routes (router.tsx nests them: a page's children are the pages after it, so the URL path is the stack
// of open lists: /settings/instances/michael/ontologies). Each element is its page, then the modals that belong to
// it on desktop (<After />: forms and details, ?params, the Sheet on phones; layouts/modals.tsx), then <Outlet />,
// the next page. Data and behaviour stay in the page components; these only wire them to the URL.
import { useCallback, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import type { TaskStatus } from "@/api";
import { Icon } from "@/components/icon";
import { IconButton } from "@/components/icon-button";
import { BookPage, PageHead } from "@/components/page";
import { InstancePage, InstancesPage, SettingsPage, useHome } from "@/components/settings";
import { useBoardFilter, useBoardView, useParam, useSearchHits, useSelection } from "@/lib/hooks";
import { BoardColumn } from "@/pages/board";
import { ChatsPage } from "@/pages/chats";
import { GraphView } from "@/pages/graph";
import { OntologiesPage } from "@/pages/ontologies";
import { SecretsList } from "@/secrets";
import { useData } from "@/store";
import { useViews } from "@/views";
import { After } from "./modals";

/** A page, the modals after it (desktop), the pages after it. */
const Then = t0 => {
  const $ = _c(6);
  const {
    page,
    children
  } = t0;
  let t1;
  if ($[0] !== page) {
    t1 = <After page={page} />;
    $[0] = page;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Outlet />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t1) {
    t3 = <>{children}{t1}{t2}</>;
    $[3] = children;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// ---- board, graph ----

export function BoardRoute() {
  const $ = _c(16);
  const snapshot = useData(_temp);
  const [filter] = useBoardFilter();
  const hits = useSearchHits(filter.q);
  const board = useBoardView(filter, hits);
  const {
    id,
    open
  } = useSelection();
  const openRef = useRef(open);
  let t0;
  if ($[0] !== open) {
    t0 = () => {
      openRef.current = open;
    };
    $[0] = open;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useLayoutEffect(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = task => openRef.current(task);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const openCard = t1;
  if (!snapshot || !board) {
    return null;
  }
  let t2;
  if ($[3] !== board.columns) {
    t2 = board.columns.map(_temp2);
    $[3] = board.columns;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const statuses = t2;
  let t3;
  if ($[5] !== board.columns || $[6] !== id || $[7] !== snapshot || $[8] !== statuses) {
    let t4;
    if ($[10] !== id || $[11] !== snapshot || $[12] !== statuses) {
      t4 = c_0 => <BoardColumn key={c_0.status} snapshot={snapshot} column={c_0} statuses={statuses} selected={id} onOpen={openCard} />;
      $[10] = id;
      $[11] = snapshot;
      $[12] = statuses;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = board.columns.map(t4);
    $[5] = board.columns;
    $[6] = id;
    $[7] = snapshot;
    $[8] = statuses;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <>{t3}</>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp2(c) {
  return c.status as TaskStatus;
}
function _temp(s) {
  return s.snapshot;
}
export function GraphRoute() {
  const $ = _c(4);
  const {
    id,
    open,
    close
  } = useSelection();
  let t0;
  if ($[0] !== close || $[1] !== id || $[2] !== open) {
    t0 = <section className="graph" data-t="graph" data-page="graph" aria-label="Graph"><GraphView selected={id} onOpen={open} onClose={close} /></section>;
    $[0] = close;
    $[1] = id;
    $[2] = open;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}

// ---- chats: a thread is a modal (?thread=) ----

export function ChatsRoute() {
  const $ = _c(5);
  const thread = useParam("thread");
  let t0;
  if ($[0] !== thread) {
    t0 = id => thread.value === id ? thread.close() : thread.open(id);
    $[0] = thread;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0 || $[3] !== thread.value) {
    t1 = <Then page="chats"><ChatsPage selected={thread.value} onOpen={t0} /></Then>;
    $[2] = t0;
    $[3] = thread.value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// ---- secrets: a secret (?sec=) and Add/Change (?secret=) are modals ----

export function SecretsRoute() {
  const $ = _c(29);
  const {
    t
  } = useTranslation();
  const [filter] = useBoardFilter();
  const sec = useParam("sec");
  const secret = useParam("secret");
  const ref = _temp3;
  let t0;
  if ($[0] !== t) {
    t0 = t("nav.secrets");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("nav.secrets");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("secretAdd");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !!secret.value;
  let t4;
  if ($[6] !== filter.project || $[7] !== secret) {
    t4 = () => secret.value ? secret.close() : secret.open(filter.project ?? "new");
    $[6] = filter.project;
    $[7] = secret;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon name="plus" size={18} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t6 = <IconButton t="secret-add" label={t2} aria-expanded={t3} onClick={t4}>{t5}</IconButton>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t6) {
    t7 = <PageHead title={t1} actions={t6} />;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== secret) {
    t8 = (...t9) => {
      const a = t9;
      return secret.open(ref(...a));
    };
    $[17] = secret;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== sec) {
    t9 = (...t10) => {
      const a_0 = t10;
      return sec.value === ref(...a_0) ? sec.close() : sec.open(ref(...a_0));
    };
    $[19] = sec;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== filter.project || $[22] !== t8 || $[23] !== t9) {
    t10 = <SecretsList project={filter.project} onChange={t8} onOpen={t9} />;
    $[21] = filter.project;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t0 || $[26] !== t10 || $[27] !== t7) {
    t11 = <Then page="secrets"><BookPage t="secrets-page" page="secrets" label={t0} className="list">{t7}{t10}</BookPage></Then>;
    $[25] = t0;
    $[26] = t10;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}

// ---- settings: /settings › instances › <instance> › ontologies; their forms and details are modals ----
function _temp3(p, profile, key) {
  return `${p}|${profile}|${key}`;
}
export function SettingsRoute() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Then page="settings"><SettingsPage /></Then>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function InstancesRoute() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Then page="instances"><InstancesPage /></Then>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function InstanceRoute() {
  const $ = _c(3);
  const name = useParams().instance;
  const t0 = `instance:${name}`;
  let t1;
  if ($[0] !== name || $[1] !== t0) {
    t1 = <Then page="instance"><InstancePage key={t0} name={name} /></Then>;
    $[0] = name;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** An instance's Ontologies (/settings/instances/<name>/ontologies; /ontologies = this home's). A connected
 *  instance's: read-only. A package is a modal (?pkg=). */
export function OntologiesRoute() {
  const $ = _c(7);
  const name = useParams().instance;
  const home = useHome();
  const remote = name && home && name !== home ? name : undefined;
  const pkg = useParam("pkg");
  const t0 = `ontologies:${remote ?? ""}`;
  let t1;
  if ($[0] !== pkg) {
    t1 = p => pkg.value === p ? pkg.close() : pkg.open(p);
    $[0] = pkg;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== pkg.value || $[3] !== remote || $[4] !== t0 || $[5] !== t1) {
    t2 = <Then page="ontologies"><OntologiesPage key={t0} instance={remote} selected={pkg.value} onOpen={t1} /></Then>;
    $[2] = pkg.value;
    $[3] = remote;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/** Old links (/connections?connect=<origin>: an invite's way in, Connect's return) land on Instances, the query
 *  kept: ?connect= opens Connect over it. */
export function ToInstances() {
  const $ = _c(2);
  const {
    search
  } = useLocation();
  let t0;
  if ($[0] !== search) {
    t0 = <Navigate to={{
      pathname: "/settings/instances",
      search
    }} replace={true} />;
    $[0] = search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** Back to the board for an unknown path. */
export const ToBoard = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Navigate to="/" replace={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/** Any other path: a kind view's page (src/views: an OAuth redirect), else the board. */
export function ViewPage() {
  const $ = _c(7);
  const views = useViews();
  const path = useLocation().pathname;
  if (!views.length) {
    return null;
  }
  let t0;
  if ($[0] !== path || $[1] !== views) {
    let t1;
    if ($[3] !== path) {
      t1 = v => v.pages?.[path];
      $[3] = path;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = views.map(t1).find(Boolean);
    $[0] = path;
    $[1] = views;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const Page = t0;
  let t1;
  if ($[5] !== Page) {
    t1 = Page ? <Page /> : <ToBoard />;
    $[5] = Page;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}